import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Inbox, RefreshCw, Search, Phone, Mail, MapPin, Clock, ChevronDown,
  CalendarPlus, UserPlus, UserCheck, ExternalLink, X
} from 'lucide-react';
import { getApiUrl } from '../../config/api';
import { getAuthHeaders, getCurrentUser } from '../../utils/auth';
import { formatPhone } from '../../utils/dateUtils';
import CreateAppointmentModal from '../Calendar/CreateAppointmentModal';
import './LeadsPage.css';

const STATUSES = ['New', 'Contacted', 'Booked', 'Not interested'];

const statusClass = (status) => `leads-status leads-status--${String(status || 'New').toLowerCase().replace(/\s+/g, '-')}`;

const formatAge = (dateStr) => {
  if (!dateStr) return '';
  const mins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatFullDate = (dateStr) => {
  if (!dateStr) return 'Unknown';
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return String(dateStr);
  return d.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const request = async (endpoint, options = {}) => {
  const response = await fetch(getApiUrl(endpoint), {
    ...options,
    headers: { 'Content-Type': 'application/json', ...getAuthHeaders(), ...options.headers }
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data.error || `Request failed (${response.status})`);
    err.data = data;
    throw err;
  }
  return data;
};

// ---------------------------------------------------------------------------
// Customer link panel: converting a lead is always a deliberate staff action
// ---------------------------------------------------------------------------
const CustomerLinkPanel = ({ lead, onLeadUpdated, onClose }) => {
  const [matches, setMatches] = useState(null);
  const [firstName, setFirstName] = useState(lead.First_name || '');
  const [lastName, setLastName] = useState(lead.Last_name || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    request(`/api/leads/consultations/${lead.ID}/customer-matches`)
      .then((rows) => !cancelled && setMatches(rows))
      .catch((err) => {
        if (cancelled) return;
        setMatches([]);
        setError(`Could not check for existing customers: ${err.message}`);
      });
    return () => { cancelled = true; };
  }, [lead.ID]);

  const run = async (fn) => {
    setBusy(true);
    setError('');
    try {
      const updated = await fn();
      onLeadUpdated(updated.lead || updated);
      onClose();
    } catch (err) {
      if (err.data?.lead) onLeadUpdated(err.data.lead);
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const linkExisting = (customerId) => run(() =>
    request(`/api/leads/consultations/${lead.ID}/link-customer`, {
      method: 'POST',
      body: JSON.stringify({ customerId })
    })
  );

  const createNew = () => run(() =>
    request(`/api/leads/consultations/${lead.ID}/convert`, {
      method: 'POST',
      body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() })
    })
  );

  return (
    <div className="leads-link-panel">
      <div className="leads-link-head">
        <strong>Link this lead to a customer</strong>
        <button type="button" className="leads-icon-btn" onClick={onClose} aria-label="Cancel">
          <X size={16} />
        </button>
      </div>

      {error && <p className="leads-error" role="alert">{error}</p>}

      <div className="leads-link-section">
        <span className="leads-field-label">Existing customers with this phone or email</span>
        {matches === null ? (
          <p className="leads-muted">Checking the customer list…</p>
        ) : matches.length === 0 ? (
          <p className="leads-muted">No existing customer matches this phone number or email.</p>
        ) : (
          <ul className="leads-match-list">
            {matches.map((c) => (
              <li key={c.ID}>
                <div>
                  <span className="leads-match-name">{c.CustomerFullName || `Customer #${c.ID}`}</span>
                  <span className="leads-muted">
                    {[c.CustomerPhone && formatPhone(c.CustomerPhone), c.CustomerEmail, c.CustomerCity]
                      .filter(Boolean).join(' · ')}
                  </span>
                </div>
                <button type="button" className="leads-btn" disabled={busy} onClick={() => linkExisting(c.ID)}>
                  <UserCheck size={15} />
                  Link this customer
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="leads-link-section">
        <span className="leads-field-label">Or add them as a new customer</span>
        <div className="leads-new-customer">
          <label>
            <span className="leads-sr-only">First name</span>
            <input
              type="text"
              className="leads-input"
              placeholder="First name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </label>
          <label>
            <span className="leads-sr-only">Last name</span>
            <input
              type="text"
              className="leads-input"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="leads-btn leads-btn--primary"
            disabled={busy || !firstName.trim()}
            onClick={createNew}
          >
            <UserPlus size={15} />
            Create new customer
          </button>
        </div>
        <p className="leads-muted">
          Uses the phone and email from this request. You can edit the rest on the customer profile.
        </p>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// One lead (collapsed row + expanded detail)
// ---------------------------------------------------------------------------
const LeadRow = ({ lead, users, isOpen, onToggle, onLeadUpdated, onBookAppointment }) => {
  const [notes, setNotes] = useState(lead.Staff_notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showLinkPanel, setShowLinkPanel] = useState(false);

  useEffect(() => {
    setNotes(lead.Staff_notes || '');
  }, [lead.Staff_notes]);

  const save = async (changes) => {
    setSaving(true);
    setError('');
    try {
      onLeadUpdated(await request(`/api/leads/consultations/${lead.ID}`, {
        method: 'PUT',
        body: JSON.stringify(changes)
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const unlinkCustomer = async () => {
    if (!window.confirm(`Unlink ${lead.CustomerName || 'this customer'} from the lead? The customer is not deleted.`)) return;
    setSaving(true);
    setError('');
    try {
      onLeadUpdated(await request(`/api/leads/consultations/${lead.ID}/link-customer`, {
        method: 'POST',
        body: JSON.stringify({ customerId: null })
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const notesDirty = notes !== (lead.Staff_notes || '');
  // Keep showing whoever is assigned even if they are no longer an active user
  const assigneeMissing = lead.Assigned_to && !users.some((u) => u.UserName === lead.Assigned_to);
  const detailId = `lead-detail-${lead.ID}`;

  return (
    <li className={`leads-item${isOpen ? ' is-open' : ''}`}>
      <button
        type="button"
        className="leads-row"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={detailId}
      >
        <span className="leads-row-main">
          <span className="leads-row-name">{lead.Full_name}</span>
          <span className="leads-row-preview">{lead.Message || 'No message'}</span>
        </span>
        <span className="leads-row-meta">
          <span className="leads-row-location">
            <MapPin size={13} aria-hidden="true" />
            {lead.Location_label || 'No location'}
          </span>
          <span className="leads-row-age" title={formatFullDate(lead.Created_At)}>{formatAge(lead.Created_At)}</span>
        </span>
        <span className={statusClass(lead.Lead_status)}>{lead.Lead_status}</span>
        <ChevronDown size={18} className="leads-row-chevron" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="leads-detail" id={detailId}>
          {error && <p className="leads-error" role="alert">{error}</p>}

          <div className="leads-detail-grid">
            <section className="leads-detail-request">
              <dl className="leads-facts">
                <div>
                  <dt>Phone</dt>
                  <dd>
                    {lead.Phone ? (
                      <a href={`tel:${lead.Phone}`} className="leads-link">
                        <Phone size={14} aria-hidden="true" />{formatPhone(lead.Phone)}
                      </a>
                    ) : 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    {lead.Email_address ? (
                      <a href={`mailto:${lead.Email_address}`} className="leads-link">
                        <Mail size={14} aria-hidden="true" />{lead.Email_address}
                      </a>
                    ) : 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt>Preferred days</dt>
                  <dd>{lead.Preferred_days || 'Not specified'}</dd>
                </div>
                <div>
                  <dt>Preferred time</dt>
                  <dd>{lead.Preferred_time || 'Not specified'}</dd>
                </div>
                <div>
                  <dt>Submitted</dt>
                  <dd>{formatFullDate(lead.Created_At)}</dd>
                </div>
                <div>
                  <dt>Came from</dt>
                  <dd>
                    {lead.Source_Name || 'website'}
                    {lead.Subscribed ? ' · joined the newsletter' : ''}
                  </dd>
                </div>
              </dl>

              <div className="leads-message">
                <span className="leads-field-label">Message</span>
                <p>{lead.Message || 'No message was included.'}</p>
              </div>

              {lead.Image_url && (
                <a href={lead.Image_url} target="_blank" rel="noopener noreferrer" className="leads-look">
                  <img src={lead.Image_url} alt="Look the customer selected" loading="lazy" />
                  <span>Selected look <ExternalLink size={12} aria-hidden="true" /></span>
                </a>
              )}
            </section>

            <section className="leads-detail-followup">
              <label className="leads-field">
                <span className="leads-field-label">Status</span>
                <select
                  className="leads-input"
                  value={lead.Lead_status}
                  disabled={saving}
                  onChange={(e) => save({ status: e.target.value })}
                >
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>

              <div className="leads-field">
                <label className="leads-field-label" htmlFor={`lead-assigned-${lead.ID}`}>Assigned to</label>
                <div className="leads-inline">
                  <select
                    id={`lead-assigned-${lead.ID}`}
                    className="leads-input"
                    value={lead.Assigned_to || ''}
                    disabled={saving}
                    onChange={(e) => save({ assignedTo: e.target.value })}
                  >
                    <option value="">Nobody yet</option>
                    {assigneeMissing && <option value={lead.Assigned_to}>{lead.Assigned_to}</option>}
                    {users.map((u) => (
                      <option key={u.UserName} value={u.UserName}>
                        {u.UserFullName && u.UserFullName !== u.UserName
                          ? `${u.UserFullName} (${u.UserName})`
                          : u.UserName}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="leads-btn"
                    disabled={saving || lead.Assigned_to === getCurrentUser()}
                    onClick={() => save({ assignedTo: getCurrentUser() })}
                  >
                    Assign to me
                  </button>
                </div>
              </div>

              <div className="leads-field">
                <label className="leads-field-label" htmlFor={`lead-notes-${lead.ID}`}>Staff notes</label>
                <textarea
                  id={`lead-notes-${lead.ID}`}
                  className="leads-input leads-textarea"
                  rows={4}
                  placeholder="Called and left a voicemail, wants a Sunday slot…"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
                <div className="leads-inline leads-inline--end">
                  {lead.Updated_By && (
                    <span className="leads-muted">
                      Last updated by {lead.Updated_By}, {formatAge(lead.Updated_At)}
                    </span>
                  )}
                  <button
                    type="button"
                    className="leads-btn"
                    disabled={saving || !notesDirty}
                    onClick={() => save({ notes })}
                  >
                    {saving ? 'Saving…' : 'Save notes'}
                  </button>
                </div>
              </div>

              <div className="leads-customer">
                <span className="leads-field-label">Customer</span>
                {lead.CustomerID ? (
                  <div className="leads-inline">
                    <Link to={`/customers/${lead.CustomerID}`} className="leads-link">
                      <UserCheck size={15} aria-hidden="true" />
                      {lead.CustomerName || `Customer #${lead.CustomerID}`}
                    </Link>
                    <button type="button" className="leads-btn leads-btn--quiet" disabled={saving} onClick={unlinkCustomer}>
                      Unlink
                    </button>
                  </div>
                ) : showLinkPanel ? (
                  <CustomerLinkPanel
                    lead={lead}
                    onLeadUpdated={onLeadUpdated}
                    onClose={() => setShowLinkPanel(false)}
                  />
                ) : (
                  <div className="leads-inline">
                    <span className="leads-muted">Not in the customer list yet.</span>
                    <button type="button" className="leads-btn" onClick={() => setShowLinkPanel(true)}>
                      <UserPlus size={15} />
                      Link or create customer
                    </button>
                  </div>
                )}
              </div>

              <div className="leads-book">
                <button
                  type="button"
                  className="leads-btn leads-btn--primary"
                  disabled={!lead.CustomerID}
                  onClick={() => onBookAppointment(lead)}
                >
                  <CalendarPlus size={16} />
                  Book appointment
                </button>
                {!lead.CustomerID && (
                  <span className="leads-muted">Link or create the customer first, then you can book.</span>
                )}
                {lead.AppointmentID && (
                  <span className="leads-muted">An appointment was already booked from this lead.</span>
                )}
              </div>
            </section>
          </div>
        </div>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const LeadsPage = () => {
  const [tab, setTab] = useState('consultations');
  const [leads, setLeads] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('Open');
  const [locationFilter, setLocationFilter] = useState('');
  const [openLeadId, setOpenLeadId] = useState(null);
  const [bookingLead, setBookingLead] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [leadRows, subscriberRows] = await Promise.all([
        request('/api/leads/consultations'),
        request('/api/leads/subscribers')
      ]);
      setLeads(leadRows);
      setSubscribers(subscriberRows);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Active users for the "Assigned to" dropdown (same list the Reminders page uses)
  useEffect(() => {
    request('/api/reminders/users')
      .then((data) => setUsers(data.users || []))
      .catch((err) => console.error('Error loading users for lead assignment:', err));
  }, []);

  const updateLead = useCallback((updated) => {
    if (!updated?.ID) return;
    setLeads((prev) => prev.map((l) => (l.ID === updated.ID ? updated : l)));
  }, []);

  const counts = useMemo(() => {
    const c = { Open: 0, All: leads.length };
    STATUSES.forEach((s) => { c[s] = 0; });
    leads.forEach((l) => {
      c[l.Lead_status] = (c[l.Lead_status] || 0) + 1;
      if (l.Lead_status === 'New' || l.Lead_status === 'Contacted') c.Open += 1;
    });
    return c;
  }, [leads]);

  const locations = useMemo(
    () => [...new Set(leads.map((l) => l.Location_label).filter(Boolean))].sort(),
    [leads]
  );

  const term = search.trim().toLowerCase();

  const visibleLeads = useMemo(() => leads.filter((l) => {
    if (statusFilter === 'Open' && l.Lead_status !== 'New' && l.Lead_status !== 'Contacted') return false;
    if (statusFilter !== 'Open' && statusFilter !== 'All' && l.Lead_status !== statusFilter) return false;
    if (locationFilter && l.Location_label !== locationFilter) return false;
    if (!term) return true;
    return [l.Full_name, l.Email_address, l.Phone, l.Message, l.Assigned_to]
      .some((v) => String(v || '').toLowerCase().includes(term));
  }), [leads, statusFilter, locationFilter, term]);

  const visibleSubscribers = useMemo(() => subscribers.filter((s) => {
    if (!term) return true;
    return [s.Email_address, s.First_name, s.Last_name, s.Source_Name]
      .some((v) => String(v || '').toLowerCase().includes(term));
  }), [subscribers, term]);

  const handleAppointmentCreated = async (appointment) => {
    const lead = bookingLead;
    setBookingLead(null);
    if (!lead) return;
    try {
      updateLead(await request(`/api/leads/consultations/${lead.ID}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'Booked', appointmentId: appointment?.ID ?? null })
      }));
    } catch (err) {
      setError(`The appointment was booked, but the lead could not be marked as Booked: ${err.message}`);
    }
  };

  const bookingPrefill = useMemo(() => {
    if (!bookingLead) return {};
    const wishes = [
      bookingLead.Preferred_days && `Preferred days: ${bookingLead.Preferred_days}`,
      bookingLead.Preferred_time && `Preferred time: ${bookingLead.Preferred_time}`,
      bookingLead.Message && `Website request: ${bookingLead.Message}`
    ].filter(Boolean).join('\n');
    return { Title: 'Consultation', EventDesc: 'Website lead', AppointmentNotes: wishes };
  }, [bookingLead]);

  const bookingCustomer = useMemo(() => bookingLead && ({
    customerName: bookingLead.CustomerName || bookingLead.Full_name,
    phone: bookingLead.Phone || '',
    customerNo: bookingLead.CustomerID
  }), [bookingLead]);

  const filterChips = ['Open', ...STATUSES, 'All'];

  return (
    <div className="leads-page">
      <header className="leads-header">
        <div>
          <h1 className="leads-title">
            <Inbox size={24} aria-hidden="true" />
            Website leads
          </h1>
          <p className="leads-subtitle">
            Consultation requests and newsletter signups from the website.
          </p>
        </div>
        <div className="leads-header-actions">
          <Link to="/appointment" className="leads-btn">
            <Clock size={15} />
            Calendar
          </Link>
          <button type="button" className="leads-btn" onClick={load} disabled={loading}>
            <RefreshCw size={15} className={loading ? 'leads-spin' : undefined} />
            Refresh
          </button>
        </div>
      </header>

      <div className="leads-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'consultations'}
          className={`leads-tab${tab === 'consultations' ? ' is-active' : ''}`}
          onClick={() => setTab('consultations')}
        >
          Consultation requests
          <span className="leads-count">{leads.length}</span>
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'subscribers'}
          className={`leads-tab${tab === 'subscribers' ? ' is-active' : ''}`}
          onClick={() => setTab('subscribers')}
        >
          Newsletter subscribers
          <span className="leads-count">{subscribers.length}</span>
        </button>
      </div>

      <div className="leads-toolbar">
        <label className="leads-search">
          <Search size={16} aria-hidden="true" />
          <span className="leads-sr-only">Search</span>
          <input
            type="search"
            placeholder={tab === 'consultations' ? 'Search name, phone, email, message' : 'Search email or name'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>

        {tab === 'consultations' && (
          <>
            <div className="leads-chips" role="group" aria-label="Filter by status">
              {filterChips.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={`leads-chip${statusFilter === s ? ' is-active' : ''}`}
                  aria-pressed={statusFilter === s}
                  onClick={() => setStatusFilter(s)}
                >
                  {s}
                  <span className="leads-count">{counts[s] || 0}</span>
                </button>
              ))}
            </div>
            <label>
              <span className="leads-sr-only">Location</span>
              <select
                className="leads-input"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
              >
                <option value="">All locations</option>
                {locations.map((loc) => <option key={loc} value={loc}>{loc}</option>)}
              </select>
            </label>
          </>
        )}
      </div>

      {error && (
        <div className="leads-error leads-error--banner" role="alert">
          {error}
          <button type="button" className="leads-btn" onClick={load}>Try again</button>
        </div>
      )}

      {loading && leads.length === 0 && subscribers.length === 0 ? (
        <ul className="leads-list" aria-busy="true" aria-label="Loading leads">
          {[0, 1, 2, 3].map((i) => <li key={i} className="leads-skeleton" />)}
        </ul>
      ) : tab === 'consultations' ? (
        visibleLeads.length === 0 ? (
          <div className="leads-empty">
            {leads.length === 0 ? (
              <>
                <strong>No consultation requests yet</strong>
                <p>
                  When someone fills in the consultation form on the website, the request shows up
                  here so you can call them back and book them in.
                </p>
              </>
            ) : (
              <>
                <strong>Nothing matches these filters</strong>
                <p>Try another status, clear the search, or choose All.</p>
              </>
            )}
          </div>
        ) : (
          <ul className="leads-list">
            {visibleLeads.map((lead) => (
              <LeadRow
                key={lead.ID}
                lead={lead}
                users={users}
                isOpen={openLeadId === lead.ID}
                onToggle={() => setOpenLeadId((id) => (id === lead.ID ? null : lead.ID))}
                onLeadUpdated={updateLead}
                onBookAppointment={setBookingLead}
              />
            ))}
          </ul>
        )
      ) : visibleSubscribers.length === 0 ? (
        <div className="leads-empty">
          <strong>{subscribers.length === 0 ? 'No newsletter subscribers yet' : 'No subscriber matches that search'}</strong>
          <p>People who join the newsletter on the website are listed here.</p>
        </div>
      ) : (
        <div className="leads-table-wrap">
          <table className="leads-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Signed up from</th>
                <th>Status</th>
                <th>Joined</th>
              </tr>
            </thead>
            <tbody>
              {visibleSubscribers.map((s) => (
                <tr key={s.Email_address}>
                  <td>
                    <a href={`mailto:${s.Email_address}`} className="leads-link">{s.Email_address}</a>
                  </td>
                  <td>{[s.First_name, s.Last_name].filter(Boolean).join(' ') || <span className="leads-muted">Not given</span>}</td>
                  <td>{s.Source_Name || 'website'}</td>
                  <td>{s.Email_status || 'Unknown'}</td>
                  <td title={formatFullDate(s.Created_At)}>{formatAge(s.Created_At)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {bookingLead && (
        <CreateAppointmentModal
          isOpen
          onClose={() => setBookingLead(null)}
          customerInfo={bookingCustomer}
          prefillData={bookingPrefill}
          customTitle={`Book ${bookingLead.Full_name}`}
          onAppointmentCreated={handleAppointmentCreated}
        />
      )}
    </div>
  );
};

export default LeadsPage;
